import * as Popover from "@radix-ui/react-popover";
import { COMMENT_TYPE } from "store/apis/notes";
import classNames from "classnames";
import { AccordionAnimation } from "components/AccordionAnimation";
import { AddCommentPop } from "components/AddComment/AddComment";
import { AddCommentPopoverRoot } from "components/AddCommentPopoverRoot/AddCommentPopoverRoot";
import { MultilevelAccordionContext } from "components/ChartOfAccountsAccordion/MultilevelAccordionContext";
import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import Dropdown from "components/DesignSystem/Dropdown/Dropdown";
import Table from "components/DesignSystem/Table/V2/Table";
import { LockedUi } from "components/LockedUi/LockedUi";
import { ArrowRight } from "components/icons/ArrowRight";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { AnimatePresence } from "framer-motion";
import React, {
  Fragment,
  HTMLAttributes,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  RowSelectionState,
  useReactTable,
} from "react-table-8.10.7";
import ThreeDots from "static/images/ThreeDots.svg";
import { openSlider } from "store/slices/transactions";
import { RootState } from "store/store";
import { GeneralLedger, ReportTxn } from "types/Models/books";

import { AddCommentAnchor } from "components/AddCommentPopoverAnchor/AddCommentPopoverAnchor";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { BankFeedIcon } from "components/icons/BankFeedIcon";
import { BillSourceIcon } from "components/icons/BillSourceIcon";
import { CreditCardIcon } from "components/icons/CreditCardIcon";
import { InvoiceSourceIcon } from "components/icons/InvoiceSourceIcon";
import { JournalEntryIcon } from "components/icons/JournalEntryIcon";
import {
  ENTRY_SOURCE,
  ENTRY_SOURCE_LABEL,
  EntrySource,
  TRANSACTION_COLUMNS,
} from "constants/transaction";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { ArrayElement } from "types/utils/ArrayElement";
import { pluralize } from "utils/pluralize";
import { stopPropagation } from "utils/stopPropagation";
import { useGetGeneralLedgerTransactionsQuery } from "store/apis/generalLedger";
import Async from "components/DesignSystem/AsyncComponents/Async";
import EntryLinkedTxnTable from "../EntryLinkedTxnTable";
import { GENERAL_LEDGER } from "constants/bookkeeping";
import ToolTip from "components/design/toolTip";
import { Padlock } from "components/icons/Padlock";

type RootTypes = {
  /**
   * Maximum indent level, default 4
   */
  maxIndent?: number;
  children: ReactNode;
} & HTMLAttributes<HTMLDivElement>;

const Root = ({ maxIndent = 4, children, ...props }: RootTypes) => {
  return (
    <MultilevelAccordionContext.Provider value={{ maxIndent }}>
      <div {...props}>{children}</div>
    </MultilevelAccordionContext.Provider>
  );
};

const Header = ({
  children,
  ...props
}: { children: ReactNode } & React.DetailedHTMLProps<
  HTMLAttributes<HTMLElement>,
  HTMLElement
>) => {
  return (
    <header
      {...props}
      className="t-py-3 t-text-text-30 t-text-subtext-sm t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-10 t-rounded-t-lg t-flex t-justify-between t-w-full t-gap-24 sm:t-gap-2 t-items-center"
    >
      {children}
    </header>
  );
};

const ENTRY_SOURCE_ICON = {
  [ENTRY_SOURCE.BANK]: BankFeedIcon,
  [ENTRY_SOURCE.CREDIT_CARD]: CreditCardIcon,
  [ENTRY_SOURCE.JOURNAL_ENTRY]: JournalEntryIcon,
  [ENTRY_SOURCE.INVOICE]: InvoiceSourceIcon,
  [ENTRY_SOURCE.BILL]: BillSourceIcon,
} as const;

const getEntrySource = (
  isLedgerEntry: boolean,
  isCreditCard: boolean,
  financialRecordType: ReportTxn["financial_record_type"]
): EntrySource => {
  if (isLedgerEntry) {
    if (financialRecordType === ENTRY_SOURCE.INVOICE) {
      return ENTRY_SOURCE.INVOICE;
    }

    if (financialRecordType === ENTRY_SOURCE.BILL) {
      return ENTRY_SOURCE.BILL;
    }

    return ENTRY_SOURCE.JOURNAL_ENTRY;
  }

  if (isCreditCard) {
    return ENTRY_SOURCE.CREDIT_CARD;
  }

  return ENTRY_SOURCE.BANK;
};

const EntriesCard = ({ entriesCount }: { entriesCount: number }) => {
  return (
    <div className="t-bg-surface-lighter-grey t-px-1.5 t-py-0.5 t-border t-border-solid t-border-neutral-0 t-rounded sm:t-text-caption">
      {entriesCount} entries
    </div>
  );
};

const TableLastRow = ({
  startDate,
  endDate,
  totalDebits,
  totalCredits,
  reportCategoryType,
}: {
  startDate: string;
  endDate: string;
  totalDebits: number;
  totalCredits: number;
  reportCategoryType: string;
}) => {
  return (
    <Table.Row className="!t-p-2 t-border-none">
      {reportCategoryType === "STANDARD" && <Table.Cell></Table.Cell>}
      <Table.Cell className="t-text-body-sm t-text-text-30 t-p-2">
        {dayjs(startDate).format(DISPLAY_DATE)} to{" "}
        {dayjs(endDate).format(DISPLAY_DATE)}
      </Table.Cell>
      <Table.Cell></Table.Cell>
      <Table.Cell></Table.Cell>
      <Table.Cell className="t-text-body-sm t-pl-2 t-text-right">
        <AmountSuperScript amount={Math.abs(totalDebits)} />
      </Table.Cell>
      <Table.Cell className="t-text-text-30 t-text-body-sm t-pl-2 t-text-right">
        <AmountSuperScript amount={totalCredits} />
      </Table.Cell>
      <Table.Cell></Table.Cell>
      <Table.Cell></Table.Cell>
    </Table.Row>
  );
};

export const GeneralLedgerTable = ({
  tableData,
  reportCategoryType,
  startDate,
  endDate,
  totalCredits,
  totalDebits,
  showLinkedTxn = false,
  entityId,
  selectedRows,
  setSelectedRows,
  hasWritePermission = true,
  isHeaderSticky = false,
}: {
  tableData: GeneralLedger["transactions"];
  reportCategoryType: string;
  startDate: string;
  endDate: string;
  totalCredits: number;
  totalDebits: number;
  showLinkedTxn?: boolean;
  entityId: string;
  selectedRows?: RowSelectionState;
  setSelectedRows?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
  hasWritePermission?: boolean;
  isHeaderSticky?: boolean;
}) => {
  const dispatch = useDispatch();

  const columnHelper =
    createColumnHelper<ArrayElement<GeneralLedger["transactions"]>>();
  const { sliderTransactions } = useSelector(
    (state: RootState) => state?.transactions
  );

  const openComment = useAppSelector((state) => state.openComment.openComment);
  const [showLinkedTxnId, setShowLinkedTxnId] = useState<string | null>(null);

  const openLinkedTxn = useCallback(
    (event: React.MouseEvent, txnId: string) => {
      event.stopPropagation();
      if (showLinkedTxnId === txnId) {
        setShowLinkedTxnId(null);
      } else {
        setShowLinkedTxnId(txnId);
      }
    },
    [showLinkedTxnId]
  );

  const selectedRowsObject = Object.entries(selectedRows || {})
    .filter(([_, selected]) => selected)
    .map(([key]) => {
      return JSON.parse(key);
    });

  const filteredSelectedRows = selectedRowsObject.filter((selectedRow) =>
    tableData.some((row) => row.uuid === selectedRow.uuid)
  );

  const getIsAllRowsSelected = () => {
    if (!selectedRows || tableData.length === 0) return false;

    return filteredSelectedRows.length === tableData.length;
  };

  const getIsSomeRowsSelected = () => {
    if (!selectedRows) return false;

    return filteredSelectedRows.length > 0 && !getIsAllRowsSelected();
  };

  const columns = useMemo(
    () => [
      ...(reportCategoryType === "STANDARD"
        ? [
            columnHelper.display({
              id: TRANSACTION_COLUMNS.SELECT,
              size: 40,
              enableSorting: false,
              header: ({ table }) => (
                <Checkbox
                  {...{
                    checked: getIsAllRowsSelected(),
                    indeterminate: getIsSomeRowsSelected(),
                    onChange: (e) => {
                      table.toggleAllRowsSelected(e.target.checked);
                    },
                  }}
                />
              ),
              cell: ({ row }) => (
                <div
                  className={classNames(
                    "group-hover:t-opacity-100 sm:t-opacity-100 t-transition-opacity",
                    {
                      "t-opacity-0": filteredSelectedRows.length === 0,
                    }
                  )}
                >
                  <Checkbox
                    {...{
                      checked: row.getIsSelected(),
                      disabled: !row.getCanSelect(),
                      indeterminate: row.getIsSomeSelected(),
                      onChange: row.getToggleSelectedHandler(),
                      key: row.id,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ),
            }),
          ]
        : []),
      columnHelper.accessor("date", {
        id: "date",
        size: 140,
        header: () => <span>Date</span>,
        cell: (info) => (
          <div className="t-text-body-sm t-text-text-60">
            {dayjs(info.getValue()).format(DISPLAY_DATE)}
          </div>
        ),
      }),
      columnHelper.accessor("merchant", {
        id: "merchant",
        size: 220,
        header: () => "Vendor",
        cell: (info) => {
          const { logo, name } = info.getValue();
          if (!Boolean(name)) {
            return "-";
          }
          return (
            <div className="t-flex t-gap-2 t-items-center t-overflow-hidden">
              <Avatar src={logo || ""} alt={name} />
              <ToolTip text={name}>
                <div className="t-text-body-sm t-text-text-60 t-truncate">
                  {name}
                </div>
              </ToolTip>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "source",
        size: 130,
        header: () => "Source",
        cell: (info) => {
          const { is_ledger_entry, is_credit_card, financial_record_type } =
            info.row.original;
          const source = getEntrySource(
            is_ledger_entry,
            is_credit_card,
            financial_record_type
          );
          const Icon = ENTRY_SOURCE_ICON[source];

          return (
            <div className="t-inline-flex t-items-center t-gap-1.5 t-whitespace-nowrap t-rounded-full t-bg-surface-lighter-grey t-py-1 t-pl-2 t-pr-3 t-text-body-sm t-text-text-60">
              <Icon />
              <span>{ENTRY_SOURCE_LABEL[source]}</span>
            </div>
          );
        },
      }),
      columnHelper.accessor("amount", {
        id: "debit",
        size: 200,
        cell: (info) => {
          if (info.getValue() < 0) {
            return (
              <div className="t-flex t-flex-col t-gap-2 t-items-end">
                <Button
                  disabled={!info.row.original.is_aggregate_entry}
                  customType="link"
                  onClick={(e) => openLinkedTxn(e, info.row.original.uuid)}
                >
                  <span
                    className={classNames(
                      "hover:t-text-purple t-text-body-sm",
                      {
                        "t-text-purple":
                          showLinkedTxnId === info.row.original.uuid,
                        "t-text-text-60":
                          showLinkedTxnId !== info.row.original.uuid,
                      }
                    )}
                  >
                    <AmountSuperScript amount={Number(info.getValue())} />
                  </span>
                </Button>
                {info.row.original.is_aggregate_entry && (
                  <span className="t-text-text-30 t-text-body-sm">
                    {pluralize(
                      info.row.original.aggregate_entry_count,
                      "transaction",
                      "transactions"
                    )}
                  </span>
                )}
              </div>
            );
          }
        },
        header: () => <div className="t-text-end t-w-full">Debit</div>,
      }),
      columnHelper.accessor("amount", {
        id: "credit",
        size: 200,
        header: () => <div className="t-text-end t-w-full">Credit</div>,
        cell: (info) => {
          if (info.getValue() >= 0) {
            return (
              <div className="t-flex t-flex-col t-gap-2 t-items-end">
                <Button
                  disabled={!info.row.original.is_aggregate_entry}
                  customType="link"
                  onClick={(e) => openLinkedTxn(e, info.row.original.uuid)}
                >
                  <span
                    className={classNames(
                      "hover:t-text-purple t-text-body-sm",
                      {
                        "t-text-purple":
                          showLinkedTxnId === info.row.original.uuid,
                        "t-text-text-60":
                          showLinkedTxnId !== info.row.original.uuid,
                      }
                    )}
                  >
                    <AmountSuperScript amount={Number(info.getValue())} />
                  </span>
                </Button>
                {info.row.original.is_aggregate_entry && (
                  <span className="t-text-text-30 t-text-body-sm">
                    {pluralize(
                      info.row.original.aggregate_entry_count,
                      "transaction",
                      "transactions"
                    )}
                  </span>
                )}
              </div>
            );
          }
        },
      }),
      columnHelper.accessor("running_total", {
        id: "running_total",
        size: 160,
        header: () => (
          <div className="t-flex t-justify-end t-w-full">Running Total</div>
        ),
        cell: (info) => {
          return (
            <div className="t-text-body-sm t-text-text-60 t-flex t-justify-end">
              <AmountSuperScript amount={Number(info.getValue())} />
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "actions",
        size: 40,
        cell: (info) => {
          return (
            <div onClick={stopPropagation}>
              <LockedUi
                hasAccess={hasWritePermission}
                blockedUi={
                  <div className="t-pointer-events-none">
                    <Button customType="ghost_icon" size="small" disabled>
                      <Padlock />
                    </Button>
                  </div>
                }
              >
                <AddCommentPopoverRoot contentId={info.row.original.uuid}>
                  <Dropdown.Root>
                    <AddCommentAnchor contentId={info.row.original.uuid}>
                      <Dropdown.Trigger asChild>
                        <div>
                          <Button customType="ghost_icon" size="small">
                            <img
                              src={ThreeDots}
                              alt="Action"
                              className="t-select-none"
                            />
                          </Button>
                        </div>
                      </Dropdown.Trigger>
                    </AddCommentAnchor>
                    <Popover.Portal>
                      <AddCommentPop
                        commentType={COMMENT_TYPE.TRANSACTION_COMMENT}
                        contentId={info.row.original.uuid}
                      />
                    </Popover.Portal>
                    <Dropdown.Portal>
                      <Dropdown.Content onClick={stopPropagation}>
                        <Popover.Trigger asChild>
                          <Dropdown.Item>Add comment</Dropdown.Item>
                        </Popover.Trigger>
                      </Dropdown.Content>
                    </Dropdown.Portal>
                  </Dropdown.Root>
                </AddCommentPopoverRoot>
              </LockedUi>
            </div>
          );
        },
      }),
    ],
    [
      showLinkedTxnId,
      openLinkedTxn,
      JSON.stringify(selectedRows),
      hasWritePermission,
    ]
  );

  const table = useReactTable({
    data: tableData || [],
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: (updater) => {
      setSelectedRows?.((prev) => {
        let next: RowSelectionState;

        if (typeof updater === "function") {
          next = updater(prev ?? {});
        } else {
          next = updater ?? {};
        }

        const updated = { ...prev };

        Object.keys(prev ?? {}).forEach((key) => {
          if (!next[key]) delete updated[key];
        });

        Object.assign(updated, next);

        return updated;
      });
    },
    state: {
      rowSelection: selectedRows || {},
    },
    getRowId: (row) =>
      JSON.stringify({
        uuid: row.uuid,
        amount: row.amount,
        date: row.date,
        running_total: row.running_total,
        merchant: {
          name: row.merchant.name,
          logo: row.merchant.logo,
        },
        description: row.description,
        is_ledger_entry: row.is_ledger_entry,
        is_credit_card: row.is_credit_card,
        from: row.from,
        is_aggregate_entry: row.is_aggregate_entry,
        aggregate_entry_count: row.aggregate_entry_count,
        category: {
          name: row.category?.name,
          uuid: row.category?.uuid,
          identifier: row.category?.identifier,
        },
      }),
  });

  const onRowClick = (
    transactionId: string,
    e: Event | React.MouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    e.stopPropagation();
    dispatch(openSlider(transactionId));
  };

  return (
    <Table.Container
      className={classNames("t-h-full t-mb-3", {
        "t-max-h-[60vh] scrollbar-visible": isHeaderSticky,
      })}
      layout="fixed"
      sticky={isHeaderSticky}
    >
      <Table.Content>
        <Table.Head>
          {table.getHeaderGroups().map((headerGroup) => (
            <Table.Row
              key={headerGroup.id}
              className="t-border t-border-y t-border-solid t-border-x-0 t-border-neutral-0"
            >
              {headerGroup.headers.map((header) => (
                <Table.HeadCell
                  key={header.id}
                  style={{ width: header.getSize() }}
                  className="t-py-2.5 t-px-2 t-text-body-sm t-text-text-30 t-uppercase t-truncate"
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </Table.HeadCell>
              ))}
            </Table.Row>
          ))}
        </Table.Head>
        <Table.Body>
          {table.getRowModel().rows.map((row) => (
            <Fragment key={row.id}>
              <Table.Row
                key={row.id}
                className={classNames(
                  "t-group hover:t-bg-surface-lighter-grey t-cursor-pointer",
                  {
                    "t-bg-surface-lighter-grey": sliderTransactions.find(
                      ({ transactionId }) => transactionId === row.original.uuid
                    ),
                    "animate-transaction-row":
                      openComment === row.original.uuid,
                  }
                )}
                onClick={(e) => onRowClick(row.original.uuid, e)}
              >
                {row.getVisibleCells().map((cell) => (
                  <Table.Cell
                    key={cell.id}
                    style={{ width: cell.column.getSize(), height: "40px" }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Cell>
                ))}
              </Table.Row>
              {showLinkedTxn && row.original.is_aggregate_entry && (
                <>
                  {row.original.uuid === showLinkedTxnId && (
                    <EntryLinkedTxnTable
                      txnId={row.original.uuid}
                      entityId={entityId}
                    />
                  )}
                </>
              )}
            </Fragment>
          ))}
          <TableLastRow
            startDate={startDate}
            endDate={endDate}
            totalCredits={totalCredits}
            totalDebits={totalDebits}
            reportCategoryType={reportCategoryType}
          />
        </Table.Body>
      </Table.Content>
    </Table.Container>
  );
};

const CategoryTransactions = ({
  categoryId,
  categoryType,
  startDate: categoryStartDate,
  endDate: categoryEndDate,
  totalDebits,
  totalCredits,
  entityId,
  selectedRows,
  setSelectedRows,
  hasWritePermission,
}: {
  categoryId: string;
  categoryType: string;
  startDate: string;
  endDate: string;
  totalDebits: number;
  totalCredits: number;
  entityId: string;
  selectedRows?: {};
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
  hasWritePermission?: boolean;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const {
    filters: {
      cashFlow,
      minAmount,
      maxAmount,
      accountingMethod,
      startDate,
      endDate,
      hideZero,
      categoryIds,
    },
  } = useSelector((state: RootState) => state.ledgerFilter);

  const filterValues = {
    cashFlow,
    minAmount: ((minAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    maxAmount: ((maxAmount.value || "") as string)
      ?.replaceAll("$", "")
      .replaceAll(",", ""),
    accountingMethod,
    startDate,
    endDate,
    hideZero,
    categoryIds,
    reportType: "GENERAL_LEDGER" as const,
  };

  const { data, isLoading, isSuccess } = useGetGeneralLedgerTransactionsQuery(
    { groupId, entityId, categoryId, ...filterValues },
    { skip: !groupId || !entityId }
  );

  const transactions = data?.transactions ?? [];

  return (
    <Async.Root
      isLoading={isLoading}
      isSuccess={isSuccess}
      isEmpty={transactions.length === 0}
      loaderType="secondary"
    >
      <Async.Empty>{null}</Async.Empty>
      <Async.Success>
        <GeneralLedgerTable
          showLinkedTxn={true}
          tableData={transactions}
          reportCategoryType={categoryType}
          startDate={categoryStartDate}
          endDate={categoryEndDate}
          totalDebits={totalDebits}
          totalCredits={totalCredits}
          entityId={entityId}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          hasWritePermission={hasWritePermission}
        />
      </Async.Success>
    </Async.Root>
  );
};

export const Trigger = ({
  data,
  level = 0,
  parentUUID,
  openCategory,
  entityId,
  selectedRows,
  setSelectedRows,
  hasWritePermission,
}: {
  data?: GeneralLedger[];
  level?: number;
  parentUUID?: string | null;
  openCategory?: string | null;
  entityId: string;
  selectedRows?: {};
  setSelectedRows?: React.Dispatch<React.SetStateAction<{}>>;
  hasWritePermission?: boolean;
}) => {
  useEffect(() => {
    const getParentTree = (
      ledger: GeneralLedger[],
      selectedCategoryId: string | null,
      currentParents: string[] = []
    ): string[] | undefined => {
      for (const category of ledger) {
        if (category.uuid === selectedCategoryId) {
          return [...currentParents, category.uuid];
        }

        if (category.types) {
          const parent = getParentTree(category.types, selectedCategoryId, [
            category.uuid,
            ...currentParents,
          ]);
          if (parent?.length) {
            return parent;
          }
        }
      }
    };

    if (openCategory && data && typeof openCategory === "string") {
      const parents = getParentTree(data, openCategory);
      if (parents && parents.length > 0) {
        setActiveAccordion(
          (prev) => new Set([...prev, ...parents, openCategory])
        );
      }
    }
  }, [openCategory, data]);

  const { maxIndent } = useContext(MultilevelAccordionContext);
  let nextLevel = level;
  if (nextLevel <= maxIndent) {
    nextLevel = nextLevel + 1;
  }

  const [activeAccordion, setActiveAccordion] = useState<Set<string>>(
    new Set()
  );

  const toggleAccordion = (uuid: string, types: GeneralLedger[]) => {
    if (!types) {
      return;
    } else if (activeAccordion.has(uuid)) {
      setActiveAccordion((prev) => {
        const newList = new Set(Array.from(prev));
        newList.delete(uuid);
        return newList;
      });
    } else {
      setActiveAccordion((prev) => {
        const newList = new Set(Array.from(prev));
        newList.add(uuid);
        return newList;
      });
    }
  };

  if (!data) {
    return null;
  }

  return (
    <Fragment>
      {data?.map((category) => {
        const {
          uuid,
          name,
          types,
          identifier,
          summary_amount,
          summary_count,
          transactions_count,
          running_total,
          start_date,
          end_date,
          total_debits,
          total_credits,
          show_data,
          opening_balance,
          category_type,
        } = category;

        return (
          <div className={classNames("t-w-full")} key={uuid}>
            <button
              className={classNames(
                "all:unset t-flex t-justify-between t-w-full t-gap-24 sm:t-gap-2 t-items-center t-rounded",
                {
                  "animate-transaction-row": openCategory === uuid,
                }
              )}
              onClick={() => toggleAccordion(uuid, types)}
            >
              <div
                className={classNames("t-flex t-flex-col t-w-full t-min-w-0", {
                  // "t-ml-4": nextLevel >= 1,
                })}
              >
                <div
                  className={classNames(
                    "t-flex t-items-center t-w-full t-gap-2 t-py-2 hover:t-bg-surface-lighter-grey t-px-2",
                    {
                      "t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-0":
                        activeAccordion.has(uuid),
                    }
                  )}
                >
                  <div className="t-flex t-gap-2 t-items-center t-min-w-0">
                    <div
                      className={classNames(
                        "t-transform t-transition t-duration-300 t-ease-in-out",
                        { "t-rotate-90": activeAccordion.has(uuid) }
                      )}
                    >
                      <ArrowRight stroke="1.5" color="currentColor" />
                    </div>
                    <div className="t-flex t-flex-col t-items-start t-gap-1 t-min-w-0">
                      <div className="t-text-text-60 t-text-body-sm t-break-words sm:t-text-left">
                        {identifier}-{name}
                      </div>
                      <div className="t-hidden sm:t-block">
                        <EntriesCard entriesCount={summary_count} />
                      </div>
                    </div>
                  </div>
                  <div className="t-text-body-sm t-flex t-gap-2 t-items-center t-ml-auto">
                    <div className="sm:t-hidden">
                      <EntriesCard entriesCount={summary_count} />
                    </div>
                    Total: <AmountSuperScript amount={summary_amount} />
                  </div>

                  <LockedUi
                    hasAccess={hasWritePermission}
                    blockedUi={
                      <div className="t-pointer-events-none">
                        <Button size="small" customType="ghost_icon" disabled>
                          <Padlock />
                        </Button>
                      </div>
                    }
                  >
                    <AddCommentPopoverRoot contentId={uuid}>
                      <Dropdown.Root>
                        <Dropdown.Trigger asChild>
                          <div>
                            <AddCommentAnchor contentId={uuid}>
                              <Button
                                size="small"
                                customType="ghost_icon"
                                title={`Action for ${name}`}
                              >
                                <img
                                  src={ThreeDots}
                                  alt="Action"
                                  className="t-select-none"
                                />
                              </Button>
                            </AddCommentAnchor>
                          </div>
                        </Dropdown.Trigger>
                        <Popover.Portal>
                          <AddCommentPop
                            commentType={COMMENT_TYPE.GENERAL_LEDGER_COMMENT}
                            contentId={uuid}
                          />
                        </Popover.Portal>
                        <Dropdown.Portal>
                          <Dropdown.Content
                            sideOffset={8}
                            side="bottom"
                            className="t-mr-16"
                          >
                            <Popover.Trigger asChild>
                              <Dropdown.Item>Add comment</Dropdown.Item>
                            </Popover.Trigger>
                          </Dropdown.Content>
                        </Dropdown.Portal>
                      </Dropdown.Root>
                    </AddCommentPopoverRoot>
                  </LockedUi>
                </div>
                {activeAccordion.has(uuid) && (
                  <div
                    className="t-text-body-sm t-text-text-60 t-w-full t-flex t-justify-end t-gap-2 t-items-center t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-0 t-py-2 t-cursor-auto t-px-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="t-flex t-gap-4">
                      Starting Balance: {"  "}
                      <AmountSuperScript amount={opening_balance} />
                    </span>
                    <div className="t-w-8 t-h-8" />
                  </div>
                )}
              </div>
            </button>
            <AnimatePresence>
              {activeAccordion.has(uuid) && types && (
                <AccordionAnimation>
                  <div
                    className={classNames({
                      "t-ml-4": nextLevel >= 1,
                    })}
                  >
                    <>
                      {transactions_count > 0 && (
                        <div className="t-mb-8">
                          {nextLevel === 1 && (
                            <div className="t-flex t-px-2 t-rounded t-flex-col">
                              <div className="t-flex t-justify-between t-items-center hover:t-bg-surface-lighter-grey t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-0 t-py-[11px] t-text-text-60 t-text-body-sm t-pl-2">
                                <div>
                                  {identifier}-{name}
                                </div>

                                <div className="t-flex t-gap-2">
                                  <span>
                                    Running Total:{" "}
                                    <AmountSuperScript amount={running_total} />
                                  </span>
                                  <div className="t-w-8 t-h-8" />
                                </div>
                              </div>
                              <div className="t-text-body-sm t-text-text-60 t-w-full t-flex t-justify-end t-gap-4 t-items-center hover:t-bg-surface-lighter-grey t-border t-border-t t-border-solid t-border-b-0 t-border-x-0 t-border-neutral-0 t-p-2">
                                <EntriesCard
                                  entriesCount={transactions_count}
                                />
                                <div className="t-flex t-gap-2 t-items-center">
                                  <span>
                                    Starting Balance: {"  "}
                                    <AmountSuperScript
                                      amount={opening_balance}
                                    />
                                  </span>
                                  <div className="t-w-8 t-h-8" />
                                </div>
                              </div>
                            </div>
                          )}
                          {show_data && (
                            <div
                              className={classNames({
                                "t-ml-4 t-w-[calc(100%-32px)]": nextLevel >= 1,
                              })}
                            >
                              <CategoryTransactions
                                categoryId={uuid}
                                categoryType={category_type}
                                startDate={start_date}
                                endDate={end_date}
                                totalDebits={total_debits}
                                totalCredits={total_credits}
                                entityId={entityId}
                                selectedRows={selectedRows}
                                setSelectedRows={setSelectedRows}
                                hasWritePermission={hasWritePermission}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <Trigger
                        data={types}
                        level={nextLevel}
                        openCategory={openCategory}
                        parentUUID={uuid}
                        entityId={entityId}
                        selectedRows={selectedRows}
                        setSelectedRows={setSelectedRows}
                        hasWritePermission={hasWritePermission}
                      />
                    </>
                  </div>
                </AccordionAnimation>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </Fragment>
  );
};

const GeneralLedgerAccordion = { Root, Trigger, Header };

export default GeneralLedgerAccordion;
